/**
 * Shared card chrome for all Agent Composer node types.
 * Draws the status ring, glyph, title, and body; type-specific content is
 * passed as children.
 */
import { memo, type ReactNode } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { ComposerNodeData, ComposerNodeType, NodeRunStatus } from '../../../types/composer';

export interface ComposerNodeCardProps extends NodeProps {
  data: ComposerNodeData;
  typeName: ComposerNodeType;
  glyph: ReactNode;
}

const STATUS_CLASS: Record<NodeRunStatus, string> = {
  idle: 'cx-status-idle',
  running: 'cx-status-running',
  succeeded: 'cx-status-succeeded',
  failed: 'cx-status-failed',
};

export const ComposerNodeCard = memo(({ data, typeName, glyph, selected, id }: ComposerNodeCardProps) => {
  const status = data.runStatus ?? 'idle';
  return (
    <div
      className={`cx-node cx-node-${typeName} ${STATUS_CLASS[status]} ${selected ? 'selected' : ''}`}
      role="group"
      aria-label={`${typeName} node ${data.label}${data.isStart ? ', start node' : ''}. Select to edit in the inspector.`}
    >
      <Handle type="target" position={Position.Left} isConnectable />
      <div className="cx-node-head">
        <span className="cx-node-glyph" aria-hidden>
          {glyph}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="cx-node-kind">{typeName}</div>
          <div className="cx-node-title" title={data.label}>
            {data.label}
          </div>
        </div>
        {data.isStart ? <span className="cx-node-start-flag">Start</span> : null}
      </div>
      <div className="cx-node-body">
        <div className="cx-node-id">{id}</div>
        <NodeSummary typeName={typeName} data={data} />
      </div>
      <Handle type="source" position={Position.Right} isConnectable />
    </div>
  );
});

ComposerNodeCard.displayName = 'ComposerNodeCard';

const NodeSummary = ({ typeName, data }: { typeName: ComposerNodeType; data: ComposerNodeData }) => {
  const cfg = data.config ?? {};
  switch (typeName) {
    case 'agent': {
      const agentId = String(cfg.agentDefinitionId ?? '');
      const message = String(cfg.message ?? '');
      return (
        <>
          <div>{agentId ? `agent · ${agentId.slice(0, 8)}…` : 'no agent selected'}</div>
          {message ? <div title={message}>{truncate(message, 42)}</div> : null}
        </>
      );
    }
    case 'condition':
      return <div>{cfg.condition ? truncate(String(cfg.condition), 48) : 'no expression'}</div>;
    case 'wait':
      return (
        <div>
          {String(cfg.waitType ?? 'Event')}
          {cfg.waitDurationSeconds != null ? ` · ${cfg.waitDurationSeconds}s` : ''}
        </div>
      );
    case 'parallel':
      return <div>fan out</div>;
    case 'merge':
      return <div>fan in</div>;
    default:
      return null;
  }
};

const truncate = (value: string, max: number) =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`;
